import { fetcher, useAuthFetch } from "matsuri-hooks"
import { useMemo } from "react"
import {
    Commodity,
    CommodityAccessory,
    CommoditySparePart,
    CommoditySparePartViewMap
} from "../domain/inventory/commodity"
import { CommodityImage } from "../domain/inventory/commodityImage"
import {
    CommodityStock,
    CommodityStockMap
} from "../domain/inventory/commodityStock"
import { m2mCore_v1 } from "../endpoints/m2m-core.v1"
import { useStorages } from "./useStorage"

export const useCommodity = (token: string, id: string | undefined) => {
    return useAuthFetch<Commodity>(
        token,
        id ? m2mCore_v1.getCommodityById({ id }) : null,
        {}
    )
}

export interface SearchConditions {
    itemTypeQuery?: string
    itemTypeIdQuery?: string
    itemTypesQuery?: string[]
    rankQuery?: string
    /**
     * 物品がまとまるグループでの絞り込み。ランク未設定も発行済みのランクと
     * 同等のグループなので、「絞り込まない」と「ランク未設定のグループに絞り込む」を
     * 区別する必要がある。
     *
     *   キーごと省略 (undefined)  絞り込まない
     *   null                      ランク未設定のグループ
     *   "R1"                      ランク R1 のグループ
     */
    rankGroup?: string | null
    nameQuery?: string
    managementId?: string
    ids?: string[]
    modelNumber?: string
    manufacturer?: string
    listingId?: string
}

export const requestSearchCommodities = (
    token: string,
    input: {
        conditions: SearchConditions
        pagination: { itemsPerPage: number; page: number }
    }
) => {
    return fetcher<{
        data: Commodity[]
        mainImages: CommodityImage[]
        total: number
    }>(m2mCore_v1.searchCommodity(), {
        method: "POST",
        body: JSON.stringify(input),
        token
    })
}

// Deprecated: Use useCommodities instead
// SWRのsuspenseを使う場合は、Suspenseでラップされた範囲を最小にする必要がある。
// さもなければ、余計な範囲を含めてLoading中の表示になってしまう。
// このプロダクトでは、現状routesでのみラップされているため、これがPromiseを投げるたびにページ全体がロード中になってしまう。
// 適切に利用する場合でも、名前をuseCommoditiesWithSuspenseとするか、suspenseのデフォルトをoffにして開発者がPromiseを投げることを意識するようにすべき。
export const useCommodities_DEPRECATED = (
    token: string,
    input: {
        conditions: SearchConditions
        pagination: { itemsPerPage: number; page: number }
    },
    options?: { suspense?: boolean }
) => {
    return useAuthFetch<{
        data: Commodity[]
        mainImages: CommodityImage[]
        stocks: CommodityStock[]
        total: number
    }>(token, m2mCore_v1.searchCommodity(), {
        method: "POST",
        body: JSON.stringify(input),
        swrConfig: { suspense: true }
    })
}

export const useCommodities = (
    token: string,
    input: {
        conditions: SearchConditions
        pagination: { itemsPerPage: number; page: number }
    },
    /**
     * enabled に false を渡すと取得しない。検索窓のように、入力があるときだけ
     * 引きたい呼び出し元のために用意している。フックは条件付きで呼べないため。
     */
    options?: { enabled?: boolean }
) => {
    const endpoint = m2mCore_v1.searchCommodity
    const enabled = options?.enabled ?? true

    return useAuthFetch<{
        data: Commodity[]
        mainImages: CommodityImage[]
        stocks: CommodityStock[]
        total: number
    }>(token, enabled ? endpoint() : null, {
        method: endpoint.method,
        body: JSON.stringify(input)
    })
}

export interface CommodityClassificationRequest {
    itemTypeId: string
    /** null はランク未設定のグループ。キーを省略した場合も同じ扱いになる。 */
    rankId?: string | null
}

export type CreateCommodityRequest = Omit<
    Commodity,
    | "id"
    | "managementId"
    | "companyId"
    | "createdAt"
    | "updatedAt"
    | "itemType"
    | "rank"
    | "classification"
> &
    CommodityClassificationRequest

export const requestCreateCommodity = (
    token: string,
    input: CreateCommodityRequest
) => {
    return fetcher<{ id: string }>(m2mCore_v1.createCommodity(), {
        token,
        method: "POST",
        body: JSON.stringify(input)
    })
}

export type UpdateCommodityRequest = CreateCommodityRequest

export const requestUpdateCommodity = (
    token: string,
    id: string,
    input: UpdateCommodityRequest
) => {
    return fetcher(m2mCore_v1.updateCommodity({ id }), {
        token,
        method: "PATCH",
        body: JSON.stringify(input)
    })
}

export const requestDeleteCommodity = (token: string, commodityId: string) => {
    return fetcher(m2mCore_v1.deleteCommodity({ commodityId }), {
        method: "DELETE",
        token
    })
}

/** サーバーが一度に受け付ける物品数の上限。 */
export const BULK_CLASSIFY_LIMIT = 500

export const requestBulkClassifyCommodities = (
    token: string,
    input: {
        commodityIds: string[]
        itemTypeId: string
        /** null はランク未設定のグループ。 */
        rankId: string | null
    }
) => {
    const endpoint = m2mCore_v1.bulkClassifyCommodities

    return fetcher<{ updatedCount: number }>(endpoint(), {
        method: endpoint.method,
        body: JSON.stringify(input),
        token
    })
}

/**
 * 上限を超える数の物品をまとめて仕分ける。
 *
 * サーバー側は1リクエストの中でしか一括して扱わないので、分割した先頭が成功して
 * 途中で失敗することがある。どこまで仕分けられたかを呼び出し元が伝えられるよう、
 * 失敗した時点で止めて成功した件数を返す。
 */
export const requestBulkClassifyCommoditiesInChunks = async (
    token: string,
    input: {
        commodityIds: string[]
        itemTypeId: string
        rankId: string | null
    }
): Promise<{ updatedCount: number; error?: Error }> => {
    let updatedCount = 0

    for (let i = 0; i < input.commodityIds.length; i += BULK_CLASSIFY_LIMIT) {
        const chunk = input.commodityIds.slice(i, i + BULK_CLASSIFY_LIMIT)
        const { data, error } = await requestBulkClassifyCommodities(token, {
            ...input,
            commodityIds: chunk
        })

        if (error) {
            return { updatedCount, error }
        }

        updatedCount += data?.updatedCount ?? chunk.length
    }

    return { updatedCount }
}

/**
 * 全ての倉庫にある在庫を取得するためのstorageId
 * このstorageIdを指定された場合、useCommodityStockByStorageをuseCommodityStockに切り替える。
 */
export const STORAGE_ID_FOR_ALL_STOCK = "STORAGE_ID_FOR_ALL_STOCK"

/**
 * 特定の物品の特定の拠点における在庫情報を取得する
 */
export const requestCommodityStockByIdAndStorageId = async (
    token: string,
    commodityId: string,
    storageId: string
) => {
    const url = m2mCore_v1.getCommodityStockByIdAndStorageId({
        id: commodityId,
        storageId
    })

    return fetcher<{ stock: CommodityStock }>(url, {
        token,
        method: "GET"
    })
}

/**
 * 物品の全拠点における在庫情報を取得する
 */
export interface CommodityStockByStorageRow {
    storageId: string
    storageName: string
    warehouseAmount: number
    availableAmount: number
    totalAmount: number
}

export const useCommodityStocksByStorage = (
    token: string,
    commodityId: string | undefined
) => {
    // アーカイブ済み倉庫の在庫も表示するため、アーカイブ済みを含めて取得する。
    const { data: storages } = useStorages(token, { includeArchived: true })

    // NOTE: 複数のfetchを投げてキャッシュする仕組みをuseAuthFetch自体はサポートしていないので、fetchをカスタマイズして実装している
    return useAuthFetch(
        token,
        storages && commodityId
            ? `useCommodityStocksByStorage_${commodityId}`
            : null,
        {
            method: "GET",
            fetch: async () => {
                if (!storages || !commodityId) return []

                const stockDataPromises = [
                    ...storages,
                    // 拠点なしの分も手動で追加する
                    {
                        id: "",
                        name: "拠点なし"
                    }
                ].map(async storage => {
                    const { data } =
                        await requestCommodityStockByIdAndStorageId(
                            token,
                            commodityId,
                            storage.id
                        )

                    if (!data?.stock) return null

                    const stock = data.stock
                    const warehouseAmount = stock.warehouseAmount ?? 0
                    const placementsReservedAmount =
                        stock.placementReservedAmount ?? 0
                    const placementPlacedAmount = stock.placedAmount ?? 0

                    return {
                        storageId: storage.id,
                        storageName: storage.name,
                        warehouseAmount,
                        availableAmount:
                            warehouseAmount - placementsReservedAmount,
                        totalAmount: warehouseAmount + placementPlacedAmount
                    }
                })

                const results = await Promise.all(stockDataPromises)
                const filteredResults = results.filter(
                    (item): item is CommodityStockByStorageRow =>
                        item !== null &&
                        (item.warehouseAmount !== 0 ||
                            item.availableAmount !== 0 ||
                            item.totalAmount !== 0)
                )

                return filteredResults
            }
        }
    )
}

export const useCommodityStockByStorage = (
    token: string,
    commodityId: string | undefined,
    storageId: string
) => {
    const endpoint = commodityId
        ? storageId === STORAGE_ID_FOR_ALL_STOCK
            ? m2mCore_v1.getCommodityStockById({ id: commodityId })
            : m2mCore_v1.getCommodityStockByIdAndStorageId({
                  id: commodityId,
                  storageId
              })
        : null

    const result = useAuthFetch<{ stock: CommodityStock } | CommodityStock>(
        token,
        endpoint,
        {}
    )

    // レスポンスの型の違いを吸収する
    return useMemo(() => {
        return {
            ...result,
            refetch: () => {
                // refetchもレスポンスの型の影響を受けるため、とりあえず型引数を使うケースを無視して対応する
                return result.refetch()
            },
            data: result.data
                ? {
                      stock:
                          "stock" in result.data
                              ? result.data.stock
                              : result.data
                  }
                : undefined
        }
    }, [result])
}

export const fetchAccessoryStocks = async (
    token: string,
    accessories: CommodityAccessory[],
    storageId: string
): Promise<CommodityStockMap> => {
    const results = await Promise.all(
        accessories.map(async accessory => {
            const endpoint =
                storageId === STORAGE_ID_FOR_ALL_STOCK
                    ? m2mCore_v1.getCommodityStockById({
                          id: accessory.commodityId
                      })
                    : m2mCore_v1.getCommodityStockByIdAndStorageId({
                          id: accessory.commodityId,
                          storageId
                      })
            const { data } = await fetcher<
                { stock: CommodityStock } | CommodityStock
            >(endpoint, { token })
            const stock = data && "stock" in data ? data.stock : data!
            return { commodityId: accessory.commodityId, stock }
        })
    )
    return new Map(results.map(r => [r.commodityId, r.stock]))
}

export const useCommoditySparePartViewMap = (
    token: string,
    spareParts: CommoditySparePart[]
): CommoditySparePartViewMap => {
    const ids = useMemo(
        () => Array.from(new Set(spareParts.map(p => p.commodityId))),
        [spareParts]
    )

    const { data: commodityRes } = useCommodities(token, {
        conditions: { ids },
        pagination: { itemsPerPage: ids.length || 1, page: 0 }
    })

    return useMemo(() => {
        const commodityById =
            commodityRes?.data?.reduce(
                (acc, c) => ({ ...acc, [c.id]: c }),
                {} as Record<string, Commodity>
            ) ?? {}

        return spareParts.reduce(
            (acc, p) => ({
                ...acc,
                [p.commodityId]: {
                    ...p,
                    name: commodityById[p.commodityId]?.name ?? p.commodityId
                }
            }),
            {} as CommoditySparePartViewMap
        )
    }, [commodityRes?.data, spareParts])
}
